/**
 * Routes a shader chat to whoever the user picked: the host (desktop app) when
 * it serves that provider, otherwise the browser transport for it.
 */

import { ShaderGenerationError, type ShaderChatMessage, type ShaderCodeModelHost, type ShaderProviderConfig, type ShaderProviderSecrets } from '../types'
import { abortedError, toShaderError } from './errors'
import { completeGenieCredits } from './genie-credits'
import { completeLocal } from './local'
import type { ChatTransportRequest } from './openai-compatible'

export { listOpenRouterModels, OPENROUTER_API_URL } from './openrouter'
export { listGrokModels, XAI_API_URL } from './grok'
export { listLocalModels } from './local'
export { GENIE_CREDITS_PROMPT_LIMIT, GENIE_CREDITS_SYSTEM_PROMPT_LIMIT } from './genie-credits'
export type { ShaderModelOption } from './openai-compatible'

/** Low temperature: code that compiles beats surprising code. */
export const SHADER_TEMPERATURE = 0.25
/** Room for a long sketch plus a reasoning model's thinking. */
export const SHADER_MAX_TOKENS = 8192

export interface CompleteShaderChatOptions {
  config: ShaderProviderConfig
  secrets: ShaderProviderSecrets
  host?: ShaderCodeModelHost | null
  system: string
  messages: ShaderChatMessage[]
  signal: AbortSignal
  onText?: (textSoFar: string) => void
  temperature?: number
  maxTokens?: number
}

export function hostServes(config: ShaderProviderConfig, host: ShaderCodeModelHost | null | undefined): host is ShaderCodeModelHost {
  return !!host && host.providers.includes(config.provider)
}

/** Genie credits is the one route with a system prompt cap. */
export function usesGenieCredits(config: ShaderProviderConfig, host?: ShaderCodeModelHost | null): boolean {
  return !hostServes(config, host) && config.provider === 'openrouter' && (config.billing ?? 'genie-credits') === 'genie-credits'
}

export async function completeShaderChat(options: CompleteShaderChatOptions): Promise<string> {
  const { config, secrets, host, signal } = options
  if (signal.aborted) throw abortedError()
  if (config.provider === 'openrouter' && config.billing === 'own-key') {
    throw new ShaderGenerationError('Personal keys are no longer used for shader authoring. Choose Magic Lamp with Genie credits before running.', 'AUTH')
  }

  if (hostServes(config, host)) {
    try {
      return await host.complete({
        provider: config.provider,
        model: config.model,
        system: options.system,
        messages: options.messages,
        signal,
        onText: options.onText,
        temperature: options.temperature ?? SHADER_TEMPERATURE,
        maxTokens: options.maxTokens ?? SHADER_MAX_TOKENS,
      })
    } catch (error) {
      throw toShaderError(error, { secrets: [secrets.openrouter, secrets.grok] })
    }
  }

  const request: ChatTransportRequest = {
    model: config.model,
    system: options.system,
    messages: options.messages,
    signal,
    onText: options.onText,
    temperature: options.temperature ?? SHADER_TEMPERATURE,
    maxTokens: options.maxTokens ?? SHADER_MAX_TOKENS,
  }
  switch (config.provider) {
    case 'openrouter':
      return completeGenieCredits(request)
    case 'grok':
      throw new ShaderGenerationError('Connect your Grok account in the desktop app or choose Magic Lamp. Personal keys are not used for shader authoring.', 'AUTH')
    case 'local':
      return completeLocal(config.baseUrl, request)
  }
}
